import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AppShell from '../components/AppShell';
import LangToggle from '../components/LangToggle';
import { apiFetch, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate } from '../lib/format';
import type { ProfileResponse, Nationality } from '../lib/types';

interface NatList { success: true; nationalities: Nationality[] }

export default function Profile() {
    const { t, i18n } = useTranslation();
    const { signOut, patchUser } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<ProfileResponse['profile'] | null>(null);
    const [nats, setNats] = useState<Nationality[]>([]);
    const [tel,   setTel]   = useState('');
    const [adrs,  setAdrs]  = useState('');
    const [ntnID, setNtnID] = useState<number | ''>('');
    const [brDt,  setBrDt]  = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [savedAt, setSavedAt] = useState<number | null>(null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [p, n] = await Promise.all([
                    apiFetch<ProfileResponse>('/api/profile'),
                    apiFetch<NatList>('/api/profile/lookups'),
                ]);
                if (cancelled) return;
                setProfile(p.profile);
                setNats(n.nationalities);
                setTel(p.profile.editable.tel ?? '');
                setAdrs(p.profile.editable.adrs ?? '');
                setNtnID(p.profile.editable.ntnID ?? '');
                setBrDt(p.profile.editable.brDt?.slice(0, 10) ?? '');
            } catch (e) {
                if (!cancelled) setErr((e as Error).message);
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, []);

    async function onSave(e: React.FormEvent) {
        e.preventDefault();
        setSaving(true); setErr(null);
        try {
            const r = await apiFetch<ProfileResponse>('/api/profile', {
                method: 'PATCH',
                body: {
                    tel:   tel.trim()  || null,
                    adrs:  adrs.trim() || null,
                    ntnID: ntnID === '' ? null : Number(ntnID),
                    brDt:  brDt || null,
                },
            });
            setProfile(r.profile);
            setSavedAt(Date.now());
        } catch (e) {
            setErr(e instanceof ApiError ? e.message : t('auth.errors.unknown'));
        } finally { setSaving(false); }
    }

    async function onLogout() {
        await signOut();
        navigate('/', { replace: true });
    }

    if (loading) return <AppShell><div className="max-w-[960px] mx-auto px-6 sm:px-12 py-12"><div className="card h-64 animate-pulse" /></div></AppShell>;
    if (!profile) return <AppShell><div className="max-w-[960px] mx-auto px-6 sm:px-12 py-12 text-ember">{err}</div></AppShell>;

    return (
        <AppShell>
            <div className="max-w-[960px] mx-auto px-6 sm:px-12 py-12 space-y-10">
                <header className="stagger">
                    <p className="eyebrow text-ember">{t('nav.profile')}</p>
                    <h1 className="font-display text-ink-on text-4xl sm:text-5xl tracking-tight mt-2">
                        {t('profile.title')}
                    </h1>
                    <p className="font-body text-ink-muted mt-3 text-balance">
                        {i18n.language === 'ar' ? (profile.readonly.nmA || profile.readonly.nm) : profile.readonly.nm}
                    </p>
                </header>

                {savedAt && <p className="text-sage text-sm">{t('profile.saved')}</p>}
                {err && <p className="text-ember">{err}</p>}

                <section className="card grid sm:grid-cols-2 gap-x-10 gap-y-5">
                    <h2 className="sm:col-span-2 eyebrow text-ember">{t('profile.readonly')}</h2>
                    <ReadOnly label={t('profile.fullName')} value={i18n.language === 'ar' ? (profile.readonly.nmA || profile.readonly.nm) : profile.readonly.nm} />
                    <ReadOnly label={t('profile.email')}    value={profile.readonly.mail} />
                    <ReadOnly label={t('profile.idNo')}     value={profile.readonly.idNo} />
                    <ReadOnly label={t('profile.psNo')}     value={profile.readonly.psNo} />
                </section>

                <section className="card">
                    <h2 className="eyebrow text-ember mb-6">{t('profile.editable')}</h2>
                    <form onSubmit={onSave} className="grid sm:grid-cols-2 gap-6">
                        <label className="block">
                            <span className="field-label">{t('profile.tel')}</span>
                            <input className="field-input" value={tel} onChange={(e) => setTel(e.target.value)} />
                        </label>
                        <label className="block">
                            <span className="field-label">{t('profile.dob')}</span>
                            <input className="field-input" type="date" value={brDt} onChange={(e) => setBrDt(e.target.value)} />
                        </label>
                        <label className="block sm:col-span-2">
                            <span className="field-label">{t('profile.adrs')}</span>
                            <textarea className="field-textarea min-h-[80px]" value={adrs} onChange={(e) => setAdrs(e.target.value)} />
                        </label>
                        <label className="block sm:col-span-2">
                            <span className="field-label">{t('profile.nationality')}</span>
                            <select className="field-input"
                                    value={ntnID === '' ? '' : String(ntnID)}
                                    onChange={(e) => setNtnID(e.target.value === '' ? '' : Number(e.target.value))}>
                                <option value="">—</option>
                                {nats.map((n) => (
                                    <option key={n.id} value={n.id}>
                                        {i18n.language === 'ar' ? (n.nmA || n.nm) : n.nm}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <div className="sm:col-span-2 flex items-center justify-end pt-4">
                            <button className="btn btn-primary" type="submit" disabled={saving}>
                                {saving ? t('common.loading') : t('common.save')}
                            </button>
                        </div>
                    </form>
                </section>

                <section className="card">
                    <h2 className="eyebrow text-ember mb-6">{t('profile.photos')}</h2>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <ImageTile  endpoint="/api/profile/photo"       label={t('profile.photo')}       has={profile.images.photo}      onUpdated={() => setSavedAt(Date.now())} />
                        <ImageTile  endpoint="/api/profile/national-id" label={t('profile.nationalId')} has={profile.images.nationalId} onUpdated={() => setSavedAt(Date.now())} />
                        <ImageTile  endpoint="/api/profile/passport"    label={t('profile.passport')}   has={profile.images.passport}   onUpdated={() => setSavedAt(Date.now())} />
                    </div>
                    <p className="text-xs text-ink-dim mt-4">{t('profile.imageLimits')}</p>
                </section>

                <section className="card">
                    <h2 className="eyebrow text-ember mb-6">{t('profile.changePassword')}</h2>
                    <ChangePasswordForm />
                </section>

                <section className="card flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="eyebrow text-ember mb-3">{t('common.language')}</h2>
                        <LangToggle onPersist={(lng) => patchUser({ lng })} />
                    </div>
                    <button type="button" onClick={onLogout}
                            className="btn btn-ghost uppercase tracking-widest2 text-xs px-5 py-2.5 self-start sm:self-auto">
                        {t('common.signOut')}
                    </button>
                </section>
            </div>
        </AppShell>
    );

    // helper render
    function ReadOnly({ label, value }: { label: string; value: string | null | undefined }) {
        return (
            <div>
                <p className="field-label">{label}</p>
                <p className="text-ink-on font-body">{value || '—'}</p>
                {brDt && label === t('profile.dob') && <span className="text-ink-muted text-xs ms-2">{fmtDate(brDt, i18n.language)}</span>}
            </div>
        );
    }
}

function ImageTile({ endpoint, label, has, onUpdated }: {
    endpoint: string; label: string; has: boolean; onUpdated: () => void;
}) {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [hasImage, setHasImage] = useState(has);
    const [err, setErr] = useState<string | null>(null);

    async function upload(f: File | null) {
        if (!f) return;
        setBusy(true); setErr(null);
        try {
            const form = new FormData();
            form.append('file', f);
            await apiFetch(endpoint, { method: 'POST', form });
            setHasImage(true);
            onUpdated();
        } catch (e) {
            setErr(e instanceof ApiError ? e.message : (e as Error).message);
        } finally { setBusy(false); }
    }

    return (
        <div className="surface p-4">
            <p className="font-display text-ink-on text-base">{label}</p>
            <p className="text-xs text-ink-muted mt-1">{hasImage ? '✓' : '—'}</p>
            <input ref={inputRef} type="file" className="hidden"
                   accept="image/jpeg,image/png,image/webp"
                   onChange={(e) => upload(e.target.files?.[0] || null)} />
            <button type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    className="btn btn-ghost text-xs mt-4 w-full">
                {busy ? t('common.loading') : (hasImage ? t('profile.replace') : t('profile.uploadImage'))}
            </button>
            {err && <p className="text-ember text-xs mt-2">{err}</p>}
        </div>
    );
}

function ChangePasswordForm() {
    const { t } = useTranslation();
    const [current, setCurrent] = useState('');
    const [next, setNext]       = useState('');
    const [confirm, setConfirm] = useState('');
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (next.length < 8)    return setErr(t('auth.errors.passwordTooShort'));
        if (next !== confirm)   return setErr(t('auth.errors.passwordsMismatch'));
        setBusy(true);
        try {
            await apiFetch('/api/auth/change-password', {
                method: 'POST',
                body: { current, new: next },
            });
            setDone(true);
            setCurrent(''); setNext(''); setConfirm('');
        } catch (e) {
            setErr(e instanceof ApiError ? e.message : t('auth.errors.unknown'));
        } finally { setBusy(false); }
    }

    return (
        <form onSubmit={submit} className="grid sm:grid-cols-3 gap-6">
            <label className="block">
                <span className="field-label">{t('profile.currentPassword')}</span>
                <input className="field-input" type="password" autoComplete="current-password"
                       value={current} onChange={(e) => setCurrent(e.target.value)} required />
            </label>
            <label className="block">
                <span className="field-label">{t('profile.newPassword')}</span>
                <input className="field-input" type="password" autoComplete="new-password"
                       value={next} onChange={(e) => setNext(e.target.value)} required />
            </label>
            <label className="block">
                <span className="field-label">{t('profile.confirmPassword')}</span>
                <input className="field-input" type="password" autoComplete="new-password"
                       value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </label>
            <div className="sm:col-span-3 flex items-center justify-between">
                {done ? <p className="text-sage text-sm">{t('profile.saved')}</p> : err ? <p className="text-ember text-sm">{err}</p> : <span />}
                <button className="btn btn-primary" type="submit" disabled={busy}>
                    {busy ? t('common.loading') : t('common.save')}
                </button>
            </div>
        </form>
    );
}
